import { ethers } from 'ethers';
import CryptoJS from 'crypto-js';
import { supabase } from '../config/supabaseClient';

// Encryption key - should be stored in environment variables
const ENCRYPTION_KEY = process.env.REACT_APP_WALLET_ENCRYPTION_KEY || 'your-default-32-char-secret-key!!';

/**
 * Every column of user_wallets the browser is allowed to read.
 *
 * Deliberately excludes encrypted_private_key and encrypted_mnemonic. Those are
 * revoked from the anon/authenticated roles (migration 015) because the key that
 * decrypts them, REACT_APP_WALLET_ENCRYPTION_KEY, ships inside the JS bundle —
 * so any column-level access to them is equivalent to publishing the keys.
 *
 * Never replace this with `select('*')`: PostgREST asks for every column and the
 * whole read fails with 42501 once the revoke is in place.
 */
const WALLET_CLIENT_COLUMNS =
    'wallet_address, provisioned_at, balance_eth, balance_usd, created_at, updated_at';

/**
 * Enhanced Crypto Wallet Service - Dual Balance System
 *
 * BALANCE TYPES:
 * 1. crypto_balance_eth: Actual deposited ETH (for master wallet collection)
 * 2. real_balance_usd: USD available for spending (off-chain transfers & purchases)
 * 
 * TRANSACTION TYPES:
 * - ON-CHAIN: deposit, withdrawal (affects both crypto and real balance)
 * - OFF-CHAIN: transfer_in, transfer_out, purchase (affects real balance only)
 */

/**
 * Encrypt sensitive data using AES encryption
 */
export const encryptData = (data) => {
    try {
        const encrypted = CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
        return encrypted;
    } catch (error) {
        console.error('Encryption error:', error);
        throw new Error('Failed to encrypt data');
    }
};

/**
 * Decrypt encrypted data
 */
export const decryptData = (encryptedData) => {
    try {
        const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        return decrypted;
    } catch (error) {
        console.error('Decryption error:', error);
        throw new Error('Failed to decrypt data');
    }
};

/**
 * Generate a new Ethereum wallet
 */
export const generateWallet = () => {
    try {
        const wallet = ethers.Wallet.createRandom();
        return {
            address: wallet.address,
            privateKey: wallet.privateKey,
            mnemonic: wallet.mnemonic.phrase,
            wallet: wallet
        };
    } catch (error) {
        console.error('Wallet generation error:', error);
        throw new Error('Failed to generate wallet');
    }
};

/**
 * Create and store encrypted wallet for a user
 * This should be called automatically on user signup
 */
export const createUserWallet = async (userId) => {
    try {
        // First, try to get existing wallet with retries (trigger might have created it)
        console.log('Checking for existing wallet with retries...');
        for (let i = 0; i < 3; i++) {
            const { data: existingWallet } = await supabase
                .from('user_wallets')
                .select('wallet_address, balance_usd, balance_eth')
                .eq('user_id', userId)
                .maybeSingle();

            // If wallet exists, return it
            if (existingWallet) {
                console.log('Found existing wallet, no need to create');
                return {
                    data: {
                        wallet_address: existingWallet.wallet_address,
                        balance_usd: existingWallet.balance_usd || 0,
                        balance_eth: existingWallet.balance_eth || 0
                    }
                };
            }
            
            // If no wallet found and we have retries left, wait a bit
            if (i < 2) {
                console.log(`Wallet not found, retry ${i + 1}/2...`);
                await new Promise(resolve => setTimeout(resolve, 800));
            }
        }

        console.log('No existing wallet found after retries, creating new one...');

        // Generate new wallet
        const walletData = generateWallet();

        // Encrypt sensitive data
        const encryptedPrivateKey = encryptData(walletData.privateKey);
        const encryptedMnemonic = encryptData(walletData.mnemonic);

        // Store wallet in database (use only basic columns for compatibility)
        const { data, error } = await supabase
            .from('user_wallets')
            .insert([
                {
                    user_id: userId,
                    wallet_address: walletData.address,
                    encrypted_private_key: encryptedPrivateKey,
                    encrypted_mnemonic: encryptedMnemonic,
                    balance_eth: 0,
                    balance_usd: 0
                }
            ])
            .select()
            .single();

        if (error) {
            // Handle duplicate key error (wallet already exists)
            if (error.code === '23505') {
                console.log('Wallet already exists, fetching existing wallet...');
                // Fetch the existing wallet
                const { data: existingData, error: fetchError } = await supabase
                    .from('user_wallets')
                    .select('wallet_address, balance_usd, balance_eth')
                    .eq('user_id', userId)
                    .maybeSingle();
                
                if (fetchError) {
                    console.error('Error fetching existing wallet:', fetchError);
                    return { error: fetchError.message };
                }
                
                if (existingData) {
                    return {
                        data: {
                            wallet_address: existingData.wallet_address,
                            balance_usd: existingData.balance_usd || 0,
                            balance_eth: existingData.balance_eth || 0
                        }
                    };
                }
            }
            
            // Handle RLS policy errors during signup (user not authenticated yet)
            if (error.code === '42501') {
                console.warn('RLS policy prevented wallet creation during signup. Wallet will be created on first login.');
                return { 
                    data: null,
                    error: null // Don't fail signup due to RLS
                };
            }
            
            console.error('Database error:', error);
            return { error: error.message };
        }

        return {
            data: {
                wallet_address: data.wallet_address,
                balance_usd: data.balance_usd || 0,
                balance_eth: data.balance_eth || 0
            }
        };
    } catch (error) {
        console.error('Create wallet error:', error);
        return { error: error.message };
    }
};

/**
 * Get user's wallet information
 * If wallet exists but is not initialized (placeholder), initialize it
 */
export const getUserWallet = async (userId, retryCount = 0) => {
    try {
        // Ensure user session is established and matches the userId
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || session.user.id !== userId) {
            if (retryCount < 3) {
                console.log('Session not ready or mismatch, waiting...');
                await new Promise(resolve => setTimeout(resolve, 500));
                return getUserWallet(userId, retryCount + 1);
            }
        }

        // Explicit column list, never `*`. encrypted_private_key and
        // encrypted_mnemonic are revoked from the client role (migration 015),
        // and `select('*')` would ask for them and fail with 42501.
        const { data, error } = await supabase
            .from('user_wallets')
            .select(WALLET_CLIENT_COLUMNS)
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(1);

        if (error) {
            console.error('getUserWallet error:', error);
            // If RLS error and we can retry, wait and try again
            if (error.code === '42501' && retryCount < 2) {
                console.log('RLS policy blocking, retrying...');
                await new Promise(resolve => setTimeout(resolve, 1000));
                return getUserWallet(userId, retryCount + 1);
            }
            return { error: error.message };
        }

        if (!data || data.length === 0) {
            // If no wallet found, wait and retry (wallet might be created by trigger)
            if (retryCount < 2) {
                console.log('No wallet found, retrying in 1 second...');
                await new Promise(resolve => setTimeout(resolve, 1000));
                return getUserWallet(userId, retryCount + 1);
            }
            console.log('No wallet found for user after retries:', userId);
            return { error: 'No wallet found' };
        }

        let wallet = data[0];

        // A wallet is only usable once the server has minted a real keypair for
        // it. The browser must never generate one: anything encrypted here uses
        // REACT_APP_WALLET_ENCRYPTION_KEY, which the server cannot decrypt, so
        // the admin sweep path would break. See api/provision-wallet.js.
        if (!wallet.provisioned_at) {
            const provisioned = await provisionWallet();
            if (provisioned.data) {
                const { data: refreshed } = await supabase
                    .from('user_wallets')
                    .select(WALLET_CLIENT_COLUMNS)
                    .eq('user_id', userId)
                    .maybeSingle();
                if (refreshed) wallet = refreshed;
            }
        }

        // Return wallet data - use whatever columns exist
        return {
            data: {
                wallet_address: wallet.wallet_address,
                provisioned_at: wallet.provisioned_at || null,
                // Use real_balance_usd if it exists, otherwise fall back to balance_usd
                real_balance_usd: parseFloat(wallet.real_balance_usd || wallet.balance_usd) || 0,
                crypto_balance_eth: parseFloat(wallet.crypto_balance_eth || wallet.balance_eth) || 0,
                balance_eth: parseFloat(wallet.balance_eth) || 0,
                balance_usd: parseFloat(wallet.balance_usd) || 0,
                created_at: wallet.created_at,
                updated_at: wallet.updated_at
            }
        };
    } catch (error) {
        console.error('Get wallet error:', error);
        return { error: error.message };
    }
};

/**
 * Ask the server to mint this user's real Ethereum keypair.
 *
 * The keypair is generated and encrypted server-side with WALLET_ENCRYPTION_KEY,
 * so the private key never reaches the browser. Idempotent — a wallet that is
 * already provisioned is returned unchanged rather than having its address
 * rotated.
 */
export const provisionWallet = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return { error: 'Not signed in' };

        const response = await fetch('/api/provision-wallet', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
            },
        });

        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
            console.error('provision-wallet failed:', body?.error || response.status);
            return { error: body?.error || 'Could not prepare your wallet address' };
        }
        return { data: body };
    } catch (error) {
        console.error('provisionWallet error:', error);
        return { error: error.message };
    }
};

/**
 * Ensure a wallet row exists, then provision it.
 *
 * This used to call the create_wallet_for_user RPC, which fabricated an address
 * from random bytes. Migration 011 made that RPC create an empty row only; the
 * real keypair comes from the provisioning endpoint.
 */
export const createWalletIfMissing = async (userId) => {
    try {
        const { error } = await supabase.rpc('create_wallet_for_user', {
            target_user_id: userId
        });
        if (error) console.error('create_wallet_for_user RPC error:', error);

        return await provisionWallet();
    } catch (error) {
        console.error('Create wallet if missing error:', error);
        return { error: error.message };
    }
};

/**
 * Get ETH to USD conversion rate with caching and fallback
 */
export const getEthToUsdRate = async () => {
    const CACHE_KEY = 'eth_rate_cache';
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    try {
        // Check cache first
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            const { rate, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
                return { data: { rate: parseFloat(rate) }, error: null };
            }
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(
            'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd',
            { signal: controller.signal }
        );
        clearTimeout(timeoutId);

        if (!response.ok) {
            throw new Error(`CoinGecko HTTP ${response.status}`);
        }

        const json = await response.json();
        const rate = json.ethereum?.usd;

        if (!rate || typeof rate !== 'number') {
            throw new Error('Invalid rate in CoinGecko response');
        }

        localStorage.setItem(CACHE_KEY, JSON.stringify({ rate, timestamp: Date.now() }));
        return { data: { rate }, error: null };
    } catch (error) {
        console.warn('ETH rate fetch failed:', error.message);
        return { data: null, error: 'Rate unavailable' };
    }
};

/**
 * Convert ETH to USD
 */
export const convertEthToUsd = async (ethAmount) => {
    const rateResult = await getEthToUsdRate();
    if (rateResult.error) {
        return { error: rateResult.error };
    }

    const usdAmount = parseFloat(ethAmount) * rateResult.data.rate;
    return {
        data: {
            usd: usdAmount.toFixed(2),
            rate: rateResult.data.rate
        }
    };
};

/**
 * Convert USD to ETH
 */
export const convertUsdToEth = async (usdAmount) => {
    const rateResult = await getEthToUsdRate();
    if (rateResult.error) {
        return { error: rateResult.error };
    }

    const ethAmount = parseFloat(usdAmount) / rateResult.data.rate;
    return {
        data: {
            eth: ethAmount.toFixed(8),
            rate: rateResult.data.rate
        }
    };
};

/**
 * DEPOSIT FLOW: On-chain deposit (updates balance_eth and balance_usd)
 * User sends ETH to their wallet address, then imports transaction manually
 */
export const processDeposit = async (userId, transactionHash) => {
    try {
        // Validate transaction hash format
        if (!transactionHash || !transactionHash.match(/^0x[a-fA-F0-9]{64}$/)) {
            return { error: 'Invalid transaction hash format. Must be 0x followed by 64 hex characters.' };
        }

        // Get user wallet
        const walletResult = await getUserWallet(userId);
        if (walletResult.error) {
            return walletResult;
        }

        const userWallet = walletResult.data.wallet_address.toLowerCase();

        // Get Ethereum provider
        const provider = getEthereumProvider();
        
        // Get transaction from blockchain
        const tx = await provider.getTransaction(transactionHash);
        if (!tx) {
            return { error: 'Transaction not found on blockchain. Please verify the transaction hash.' };
        }

        // Verify transaction is to user's wallet
        if (!tx.to || tx.to.toLowerCase() !== userWallet) {
            return { error: 'This transaction was sent to a different wallet. Please make sure you\'re importing a deposit to your wallet.' };
        }

        // Check if transaction has value
        if (!tx.value || tx.value === 0n) {
            return { error: 'Transaction has no ETH value. Only ETH deposits can be imported.' };
        }

        // Get transaction receipt (check if confirmed)
        const receipt = await provider.getTransactionReceipt(transactionHash);
        if (!receipt) {
            return { error: 'Transaction not yet confirmed. Please wait for confirmation and try again.' };
        }

        if (receipt.status === 0) {
            return { error: 'Transaction failed on blockchain. Cannot import failed transactions.' };
        }

        // Calculate amounts
        const amountEth = parseFloat(ethers.formatEther(tx.value));
        const conversionResult = await convertEthToUsd(amountEth);
        const amountUsd = conversionResult.data ? parseFloat(conversionResult.data.usd) : 0;

        // Check if database function exists, otherwise use direct insert
        console.log('Attempting to call process_blockchain_deposit RPC function...');
        const { data: result, error: rpcError } = await supabase.rpc('process_blockchain_deposit', {
            p_user_id: userId,
            p_transaction_hash: transactionHash,
            p_from_address: tx.from,
            p_to_address: tx.to,
            p_amount_eth: amountEth,
            p_amount_usd: amountUsd,
            p_block_number: receipt.blockNumber
        });

        if (rpcError) {
            console.error('RPC Error details:', rpcError);
            console.log('RPC failed, using fallback method');
            
            // If function doesn't exist, fall back to direct database insert
            if (rpcError.message && (rpcError.message.includes('function') || rpcError.message.includes('does not exist'))) {
                console.log('Database function not found, using direct insert method');
                
                // Get user's wallet address
                const { data: walletData, error: walletError } = await supabase
                    .from('user_wallets')
                    .select('wallet_address')
                    .eq('user_id', userId)
                    .maybeSingle();

                console.log('Wallet data:', walletData);

                if (walletError || !walletData) {
                    console.error('Wallet lookup error:', walletError);
                    return { error: 'Could not find user wallet' };
                }

                const userWalletAddress = walletData.wallet_address;
                
                if (!userWalletAddress) {
                    console.error('Wallet address is null for user:', userId);
                    return { error: 'Your wallet address is not set. Please contact support.' };
                }

                // Check for duplicate first
                const { data: existing } = await supabase
                    .from('wallet_transactions')
                    .select('id')
                    .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`)
                    .eq('transaction_hash', transactionHash)
                    .maybeSingle();

                if (existing) {
                    return { error: 'This transaction has already been imported.' };
                }

                // Insert transaction
                const { data: txData, error: insertError } = await supabase
                    .from('wallet_transactions')
                    .insert({
                        to_user_id: userId,  // For deposits, user is recipient
                        wallet_address: userWalletAddress,
                        transaction_type: 'deposit',
                        amount_eth: amountEth,
                        amount_usd: amountUsd,
                        transaction_hash: transactionHash,
                        from_address: tx.from,
                        to_address: tx.to,
                        block_number: receipt.blockNumber,
                        is_onchain: true,
                        status: 'confirmed',
                        description: 'Manual import: On-chain deposit',
                        confirmed_at: new Date().toISOString()
                    })
                    .select()
                    .single();

                if (insertError) {
                    console.error('Insert error:', insertError);
                    if (insertError.code === '23505') { // Duplicate key
                        return { error: 'This transaction has already been imported.' };
                    }
                    return { error: insertError.message };
                }

                // Update wallet balances
                const { error: updateError } = await supabase
                    .from('user_wallets')
                    .update({
                        balance_eth: supabase.raw(`balance_eth + ${amountEth}`),
                        balance_usd: supabase.raw(`balance_usd + ${amountUsd}`),
                        updated_at: new Date().toISOString()
                    })
                    .eq('user_id', userId);

                if (updateError) {
                    console.error('Balance update error:', updateError);
                    return { error: 'Transaction recorded but balance update failed: ' + updateError.message };
                }

                // Fire-and-forget deposit notification emails. The endpoint now
                // requires the recipient's own token and reads the amounts from
                // the ledger, so they are no longer sent from here.
                const ethRateResult2 = await getEthToUsdRate();
                const { data: { session: depositSession2 } } = await supabase.auth.getSession();
                fetch('/api/send-deposit-notification', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${depositSession2?.access_token || ''}`,
                    },
                    body: JSON.stringify({
                        userId,
                        txHash: transactionHash,
                        ethRate: ethRateResult2.data?.rate || 3000,
                    }),
                }).catch(() => {});

                return {
                    data: {
                        transaction_id: txData.id,
                        amount_eth: amountEth,
                        amount_usd: amountUsd,
                        message: 'Transaction imported successfully'
                    }
                };
            }
            
            // Check if it's a duplicate transaction error
            if (rpcError.message && rpcError.message.includes('unique_transaction_hash_per_user')) {
                return { error: 'This transaction has already been imported.' };
            }
            
            return { error: rpcError.message || 'Failed to process deposit' };
        }

        // result is a JSON object from the database function
        if (result && result.length > 0) {
            const row = result[0];
            if (row.success) {
                // Fire-and-forget deposit notification emails (user + admin).
                // Amounts are read server-side from the ledger row, not sent.
                const ethRateResult = await getEthToUsdRate();
                const { data: { session: depositSession } } = await supabase.auth.getSession();
                fetch('/api/send-deposit-notification', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${depositSession?.access_token || ''}`,
                    },
                    body: JSON.stringify({
                        userId,
                        txHash: transactionHash,
                        ethRate: ethRateResult.data?.rate || 3000,
                    }),
                }).catch(() => {});

                return {
                    data: {
                        transaction_id: row.transaction_id,
                        amount_eth: amountEth,
                        amount_usd: amountUsd,
                        message: row.message
                    }
                };
            } else {
                return { error: row.message || 'Failed to process deposit' };
            }
        } else {
            return { error: 'No result returned from database function' };
        }
    } catch (error) {
        console.error('Process deposit error:', error);
        
        // Provide user-friendly error messages
        if (error.message.includes('network')) {
            return { error: 'Network error. Please check your internet connection and try again.' };
        }
        if (error.message.includes('rate limit')) {
            return { error: 'API rate limit reached. Please try again in a few moments.' };
        }
        
        return { error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * SYNC ON-CHAIN TRANSACTIONS: Check for new incoming transactions
 * Simplified version - checks specific transaction hash instead of scanning
 */
export const syncOnChainTransactions = async (userId) => {
    try {
        console.log('Automatic sync is limited to avoid rate limits. Use manual import for specific transactions.');
        return { data: { synced: 0 }, error: null };
    } catch (error) {
        console.error('Sync on-chain transactions error:', error);
        return { error: error.message };
    }
};

/**
 * MANUAL IMPORT: Import a specific transaction by hash
 * Use this to add on-chain deposits without scanning entire blockchain
 */
export const importTransactionByHash = async (userId, transactionHash) => {
    try {
        console.log(`Importing transaction: ${transactionHash}`);

        // Check if we've already recorded this transaction
        const { data: existing } = await supabase
            .from('wallet_transactions')
            .select('id')
            .eq('transaction_hash', transactionHash)
            .maybeSingle();

        if (existing) {
            return { 
                data: { imported: false, message: 'Transaction already exists' }, 
                error: null 
            };
        }

        // Process the deposit
        const result = await processDeposit(userId, transactionHash);
        
        if (result.error) {
            return { error: result.error };
        }

        console.log(`Successfully imported transaction: ${transactionHash}`);
        return { 
            data: { imported: true, message: 'Transaction imported successfully' }, 
            error: null 
        };
    } catch (error) {
        console.error('Import transaction error:', error);
        return { error: error.message };
    }
};

/**
 * USER-TO-USER TRANSFER: Off-chain transfer (updates real balance only)
 * Sender's real_balance_usd is debited, receiver's real_balance_usd is credited
 */
export const transferToUser = async (fromUserId, toUserIdentifier, amountUsd, notes = '') => {
    try {
        // Validate amount
        const amount = parseFloat(amountUsd);
        if (amount <= 0) {
            return { error: 'Invalid amount' };
        }

        // Get sender wallet and check balance
        const senderWalletResult = await getUserWallet(fromUserId);
        if (senderWalletResult.error) {
            return senderWalletResult;
        }

        if (senderWalletResult.data.real_balance_usd < amount) {
            return { error: 'Insufficient balance' };
        }

        // Find receiver by user_id or email
        let receiverUserId;
        if (toUserIdentifier.includes('@')) {
            // Use database function to look up user by email
            const { data, error: rpcError } = await supabase
                .rpc('get_user_id_by_email', { user_email: toUserIdentifier.toLowerCase() });

            if (rpcError || !data) {
                console.error('User lookup error:', rpcError);
                return { error: 'User not found with that email address' };
            }
            
            receiverUserId = data;
        } else {
            // Assume it's a user_id
            receiverUserId = toUserIdentifier;
        }

        // Check if receiver exists and has a wallet using SECURITY DEFINER function
        const { data: hasWallet, error: walletCheckError } = await supabase
            .rpc('user_has_wallet', { check_user_id: receiverUserId });

        if (walletCheckError || !hasWallet) {
            console.error('Receiver wallet check error:', walletCheckError);
            return { error: 'Receiver does not have a wallet. They must log in once to create their wallet.' };
        }

        // Prevent self-transfer
        if (fromUserId === receiverUserId) {
            return { error: 'Cannot transfer to yourself' };
        }

        // Get receiver's wallet address using SECURITY DEFINER function
        const { data: receiverWalletAddress, error: receiverAddressError } = await supabase
            .rpc('get_wallet_address', { check_user_id: receiverUserId });

        if (receiverAddressError || !receiverWalletAddress) {
            // The common cause is not an error at all: the recipient signed up
            // but has never signed in, so their wallet row exists with a NULL
            // address and no keypair has been minted yet. "Receiver wallet not
            // accessible" told the sender nothing they could act on.
            if (receiverAddressError) {
                console.error('Could not fetch receiver wallet address:', receiverAddressError);
            }
            return {
                error: "This user hasn't finished setting up their account yet. " +
                       'Ask them to sign in once, then try again.'
            };
        }

        // Get receiver user info for better description
        const { data: receiverUser } = await supabase
            .from('users')
            .select('full_name, email')
            .eq('id', receiverUserId)
            .single();
        
        const receiverName = (receiverUser && (receiverUser.full_name || receiverUser.email?.split('@')[0])) || 'User';

        // Get sender user info for better description
        const { data: senderUser } = await supabase
            .from('users')
            .select('full_name, email')
            .eq('id', fromUserId)
            .single();
        
        const senderName = (senderUser && (senderUser.full_name || senderUser.email?.split('@')[0])) || 'User';

        // Use SECURITY DEFINER RPC to insert both records and update balances atomically
        const { data: transferResult, error: transferError } = await supabase
            .rpc('process_offchain_transfer', {
                p_from_user_id: fromUserId,
                p_to_user_id: receiverUserId,
                p_amount: amount,
                p_from_wallet: senderWalletResult.data.wallet_address,
                p_to_wallet: receiverWalletAddress,
                p_sender_name: senderName,
                p_receiver_name: receiverName,
            });

        if (transferError) {
            return { error: transferError.message };
        }

        if (transferResult?.error) {
            return { error: transferResult.error };
        }

        return {
            data: {
                sender_transaction: { id: transferResult.sender_tx_id },
                receiver_transaction: { id: transferResult.receiver_tx_id },
                amount_usd: amount
            }
        };
    } catch (error) {
        console.error('Transfer to user error:', error);
        return { error: error.message };
    }
};

/**
 * Get Ethereum provider based on environment configuration
 */
const getEthereumProvider = () => {
    const network = process.env.REACT_APP_ETHEREUM_NETWORK || 'mainnet';

    if (process.env.REACT_APP_INFURA_PROJECT_ID && process.env.REACT_APP_INFURA_PROJECT_ID !== 'YOUR_INFURA_PROJECT_ID_HERE') {
        return new ethers.JsonRpcProvider(
            `https://${network}.infura.io/v3/${process.env.REACT_APP_INFURA_PROJECT_ID}`
        );
    }
    
    if (process.env.REACT_APP_ALCHEMY_API_KEY && process.env.REACT_APP_ALCHEMY_API_KEY !== 'YOUR_ALCHEMY_API_KEY_HERE') {
        const alchemyNetwork = network === 'mainnet' ? 'eth-mainnet' : `eth-${network}`;
        return new ethers.JsonRpcProvider(
            `https://${alchemyNetwork}.g.alchemy.com/v2/${process.env.REACT_APP_ALCHEMY_API_KEY}`
        );
    }
    
    if (process.env.REACT_APP_CUSTOM_RPC_URL) {
        return new ethers.JsonRpcProvider(process.env.REACT_APP_CUSTOM_RPC_URL);
    }
    
    console.warn('No Infura/Alchemy API key found. Using public nodes (may be slower).');
    return ethers.getDefaultProvider(network);
};

/**
 * Get user's transaction history with on-chain/off-chain indicators
 */
export const getUserTransactions = async (userId, limit = 50) => {
    try {
        // Exclude referral_bonus rows where user is only the trigger (from_user_id),
        // not the recipient — those bonuses belong to the referrer's feed only.
        const { data, error } = await supabase
            .from('wallet_transactions')
            .select('*')
            .or(`to_user_id.eq.${userId},and(from_user_id.eq.${userId},transaction_type.neq.referral_bonus)`)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (error) {
            return { error: error.message };
        }

        // For each transaction, fetch the other user's info based on wallet address
        const enrichedTransactions = await Promise.all(data.map(async (tx) => {
            if (tx.transaction_type === 'transfer_out' && tx.to_address) {
                // Get recipient info - use maybeSingle to avoid 406 errors
                const { data: recipientWallet } = await supabase
                    .from('user_wallets')
                    .select('user_id')
                    .eq('wallet_address', tx.to_address)
                    .maybeSingle();
                
                if (recipientWallet) {
                    const { data: recipientUser } = await supabase
                        .from('users')
                        .select('full_name, email')
                        .eq('id', recipientWallet.user_id)
                        .maybeSingle();
                    
                    if (recipientUser) {
                        // Use full_name if available, otherwise use email username
                        const recipientName = recipientUser.full_name || recipientUser.email?.split('@')[0] || 'User';
                        tx.recipient_name = recipientName;
                    }
                }
            } else if (tx.transaction_type === 'transfer_in' && tx.from_address) {
                // Get sender info - use maybeSingle to avoid 406 errors
                const { data: senderWallet, error: walletError } = await supabase
                    .from('user_wallets')
                    .select('user_id')
                    .eq('wallet_address', tx.from_address)
                    .maybeSingle();
                
                console.log('Sender wallet lookup:', senderWallet, walletError);
                
                if (senderWallet) {
                    const { data: senderUser, error: userError } = await supabase
                        .from('users')
                        .select('full_name, email')
                        .eq('id', senderWallet.user_id)
                        .maybeSingle();
                    
                    console.log('Sender user lookup:', senderUser, userError);
                    
                    if (senderUser) {
                        // Use full_name if available, otherwise use email username
                        const senderName = senderUser.full_name || senderUser.email?.split('@')[0] || 'User';
                        tx.sender_name = senderName;
                        console.log('Set sender_name to:', senderName);
                    }
                }
            }
            return tx;
        }));

        return { data: enrichedTransactions };
    } catch (error) {
        console.error('Get transactions error:', error);
        return { error: error.message };
    }
};

const walletServiceEnhanced = {
    generateWallet,
    createUserWallet,
    getUserWallet,
    getEthToUsdRate,
    convertEthToUsd,
    convertUsdToEth,
    processDeposit,
    syncOnChainTransactions,
    importTransactionByHash,
    transferToUser,
    getUserTransactions,
    encryptData,
    decryptData
};

export default walletServiceEnhanced;
